import React, { useEffect, useRef, useState } from 'react';
import Icon from './Icons';
import SmsConsent from './SmsConsentCheckbox';
import { formatPhone, phoneDigits } from '../../lib/submit';

const EMPTY = { fullName: '', companyName: '', email: '', phoneNumber: '', message: '' };
const NO_CONSENT = { smsCustomerCare: false, smsMarketing: false };

/**
 * Shared demo-only contact and SMS form. Displays a local success panel on
 * valid submit. No request is sent and no information/consent is stored.
 */
export default function ContactRequestForm({ mode = 'contact' }) {
  const smsPage = mode === 'sms-opt-in';
  const confirmationRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(NO_CONSENT);
  const [validation, setValidation] = useState('');
  const wantsSms = consent.smsCustomerCare || consent.smsMarketing;
  const set = (key) => (event) => {
    setValidation('');
    setForm((current) => ({ ...current, [key]: key === 'phoneNumber' ? formatPhone(event.target.value) : event.target.value }));
  };
  const setConsentField = (key, selected) => {
    setValidation('');
    setConsent((current) => ({ ...current, [key]: selected }));
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setValidation('');
    // Visual demo only: validate the form, then show the confirmation locally.
    // No backend call, email delivery, or consent record is created.
    if (wantsSms && phoneDigits(form.phoneNumber).length !== 10) {
      setValidation('Enter a valid 10-digit US mobile number for SMS updates, or uncheck the SMS boxes.');
      return;
    }
    setSubmitted(true);
  };

  useEffect(() => {
    if (!submitted || !confirmationRef.current) return undefined;
    // Reveal is managed by the parent page only on its initial mount. A new
    // `.reveal` panel inserted here was stuck at opacity: 0, appearing blank.
    // Focus and scroll the visible confirmation into view after the form shrinks.
    const frame = window.requestAnimationFrame(() => {
      const panel = confirmationRef.current;
      if (!panel) return;
      panel.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      const heading = panel.querySelector('[data-confirmation-heading]');
      if (heading) heading.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [submitted]);

  const reset = () => {
    setForm(EMPTY);
    setConsent(NO_CONSENT);
    setValidation('');
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <div ref={confirmationRef} className="card scroll-mt-28 p-7 sm:p-9" role="status" aria-live="polite">
        <div className="eyebrow mb-2">{smsPage ? 'SMS consent form' : 'Send a message'}</div>
        <h2 className="h3">Every inquiry gets a reply.</h2>
        <div className="mt-8 rounded-xl border border-green-200 bg-green-50 p-6 text-green-900">
          <h3 data-confirmation-heading tabIndex={-1} className="flex items-center gap-2 font-display text-xl font-bold text-green-900 focus:outline-none">
            <Icon name="check" className="h-5 w-5" /> Message submitted successfully!
          </h3>
          <p className="mt-2 text-[0.92rem]">
            Thank you for contacting Bitnex Freight LLC. We have received your request.
          </p>
          <button type="button" onClick={reset} className="mt-4 inline-block text-[0.9rem] font-bold underline">Send another</button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="card scroll-mt-28 p-7 sm:p-9"
      id={smsPage ? 'opt-in-form' : 'contact-form'}
      onSubmit={onSubmit}
    >
      <div className="eyebrow mb-2">{smsPage ? 'SMS consent form' : 'Send a message'}</div>
      <h2 className="h3">{smsPage ? 'Choose your SMS preferences.' : 'Every inquiry gets a reply.'}</h2>
      <p className="mt-2 text-[0.9rem] text-ink-600">
        {smsPage
          ? 'Complete the fields below and check the text-message types you want. Both boxes are unchecked by default.'
          : 'Fields marked * are required. SMS consent is optional and is not required to request trucking or freight services.'}
      </p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor={`${mode}-name`}>Full name *</label>
          <input id={`${mode}-name`} name="fullName" className="input" value={form.fullName} onChange={set('fullName')} required maxLength={120} autoComplete="name" placeholder="Jane Carrier" />
        </div>
        <div>
          <label className="label" htmlFor={`${mode}-company`}>Company / MC (optional)</label>
          <input id={`${mode}-company`} name="companyName" className="input" value={form.companyName} onChange={set('companyName')} maxLength={140} autoComplete="organization" placeholder="XYZ Trucking · MC 123456" />
        </div>
        <div>
          <label className="label" htmlFor={`${mode}-email`}>Email {smsPage ? '(optional)' : '*'}</label>
          <input id={`${mode}-email`} name="email" type="email" className="input" value={form.email} onChange={set('email')} required={!smsPage} maxLength={180} autoComplete="email" placeholder="you@company.com" />
        </div>
        <div>
          <label className="label" htmlFor={`${mode}-phone`}>{wantsSms ? 'Mobile phone number *' : 'Mobile phone number (optional)'}</label>
          <input id={`${mode}-phone`} name="phoneNumber" type="tel" inputMode="tel" className="input" value={form.phoneNumber} onChange={set('phoneNumber')} required={wantsSms} autoComplete="tel" placeholder="(555) 555-5555" />
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor={`${mode}-message`}>How can we help? {smsPage ? '(optional)' : '*'}</label>
          <textarea id={`${mode}-message`} name="message" className="input min-h-[140px]" value={form.message} onChange={set('message')} required={!smsPage} maxLength={2000} placeholder={smsPage ? 'Any questions about your SMS preferences? (optional)' : 'Freight type, route, shipment dates, or your question…'} />
        </div>
        <div className="sm:col-span-2">
          <SmsConsent values={consent} onChange={setConsentField} idPrefix={smsPage ? 'optin' : 'contact'} />
        </div>
        {validation && <p role="alert" className="text-[0.9rem] font-semibold text-red-700 sm:col-span-2">{validation}</p>}
        <div className="sm:col-span-2">
          <button type="submit" className="btn-primary w-full sm:w-auto">
            Submit message <Icon name="arrowRight" className="h-4 w-4" />
          </button>
        </div>
      </div>
    </form>
  );
}
